import { Component, EventEmitter, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from '@app/services/auth.service';

type AuthView = 'login' | 'signup' | 'forgot';

@Component({
  selector: 'app-auth-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './auth-modal.html',
  styleUrl: './auth-modal.css',
})
export class AuthModal {
  @Output() authenticated = new EventEmitter<void>();

  view = signal<AuthView>('login');
  email = '';
  password = '';
  confirmPassword = '';
  loading = false;
  errorMsg = '';
  successMsg = '';

  constructor(private auth: AuthService) {}

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }

  setView(v: AuthView): void {
    this.view.set(v);
    this.errorMsg = '';
    this.successMsg = '';
    this.password = '';
    this.confirmPassword = '';
  }

  async onLogin(): Promise<void> {
    if (!this.email || !this.password) { this.errorMsg = 'Please fill in all fields.'; return; }
    if (!this.isValidEmail(this.email)) { this.errorMsg = 'Enter a valid email address.'; return; }
    this.loading = true; this.errorMsg = '';
    const { error } = await this.auth.signInWithEmail(this.email, this.password);
    this.loading = false;
    if (error) { this.errorMsg = this.friendlyError(error.message); return; }
    this.authenticated.emit();
  }

  async onSignup(): Promise<void> {
    if (!this.email || !this.password) { this.errorMsg = 'Please fill in all fields.'; return; }
    if (!this.isValidEmail(this.email)) { this.errorMsg = 'Enter a valid email address.'; return; }
    if (this.password !== this.confirmPassword) { this.errorMsg = 'Passwords do not match.'; return; }
    if (this.password.length < 8) { this.errorMsg = 'Password must be at least 8 characters.'; return; }
    this.loading = true; this.errorMsg = '';
    const { error } = await this.auth.signUpWithEmail(this.email, this.password);
    this.loading = false;
    if (error) { this.errorMsg = this.friendlyError(error.message); return; }
    this.successMsg = 'Check your email to confirm your account, then sign in.';
    this.setView('login');
  }

  async onForgot(): Promise<void> {
    if (!this.email) { this.errorMsg = 'Enter your email address.'; return; }
    if (!this.isValidEmail(this.email)) { this.errorMsg = 'Enter a valid email address.'; return; }
    this.loading = true; this.errorMsg = '';
    // Store the current page so the reset-password page can send the user back here
    // after the flow completes (successful reset, oauth-only, or expired link).
    sessionStorage.setItem('pxlvd-auth-return', window.location.pathname + window.location.search);
    const { error } = await this.auth.resetPassword(this.email);
    this.loading = false;
    if (error) {
      sessionStorage.removeItem('pxlvd-auth-return');
      this.errorMsg = this.friendlyError(error.message);
      return;
    }
    this.successMsg = 'Password reset link sent - check your email.';
  }

  async onGoogle(): Promise<void> {
    this.loading = true; this.errorMsg = '';
    const { error } = await this.auth.signInWithGoogle();
    this.loading = false;
    if (error) { this.errorMsg = this.friendlyError(error.message); }
    // Google OAuth redirects away - no emit needed here
  }

  onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      if (this.view() === 'login') this.onLogin();
      else if (this.view() === 'signup') this.onSignup();
      else if (this.view() === 'forgot') this.onForgot();
    }
  }

  private friendlyError(msg: string): string {
    if (msg.includes('Invalid login credentials')) return 'Incorrect email or password.';
    if (msg.includes('Email not confirmed')) return 'Please confirm your email before signing in.';
    if (msg.includes('User already registered')) return 'An account with this email already exists.';
    if (msg.includes('Password should be')) return 'Password must be at least 8 characters.';
    if (msg.includes('rate limit')) return 'Too many attempts. Please wait a moment.';
    return msg;
  }
}
